import { useState, useCallback, useEffect } from 'react'

interface Toast {
  id: string
  title: string
  description?: string
  variant?: 'default' | 'destructive'
}

interface ToastState {
  toasts: Toast[]
}

// Simple toast implementation
// In production, you might want to use a more robust solution like react-hot-toast
let toastCounter = 0
const listeners = new Set<(state: ToastState) => void>()
let memoryState: ToastState = { toasts: [] }

function dispatch(toast: Omit<Toast, 'id'>) {
  const id = String(toastCounter++)
  const newToast = { ...toast, id }

  memoryState = {
    toasts: [...memoryState.toasts, newToast],
  }

  listeners.forEach((listener) => listener(memoryState))

  // Auto dismiss after 5 seconds
  setTimeout(() => {
    memoryState = {
      toasts: memoryState.toasts.filter((t) => t.id !== id),
    }
    listeners.forEach((listener) => listener(memoryState))
  }, 5000)

  return id
}

export function useToast() {
  const [state, setState] = useState<ToastState>(memoryState)

  useEffect(() => {
    listeners.add(setState)
    return () => {
      listeners.delete(setState)
    }
  }, [])

  const toast = useCallback(
    ({ title, description, variant = 'default' }: Omit<Toast, 'id'>) => {
      return dispatch({ title, description, variant })
    },
    [],
  )

  const dismiss = useCallback((toastId: string) => {
    memoryState = {
      toasts: memoryState.toasts.filter((t) => t.id !== toastId),
    }
    listeners.forEach((listener) => listener(memoryState))
  }, [])

  return {
    toast,
    dismiss,
    toasts: state.toasts,
  }
}

export type { Toast }
